import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Components
import Landing from "./components/Landing";
import Dashboard from "./components/Dashboard";
import OrishaGame from "./components/games/OrishaGame";
import IfaSignsGame from "./components/games/IfaSignsGame";
import RitualsGame from "./components/games/RitualsGame";
import YorubaLanguageGame from "./components/games/YorubaLanguageGame";
import SureyeGame from "./components/games/SureyeGame";
import ComingSoon from "./components/ComingSoon";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import UserProfile from "./components/UserProfile";
import AdminPanel from "./components/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      toast.success("¡Bienvenido de vuelta!");
      return { success: true };
    } catch (error) {
      let message = "Error al iniciar sesión";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle array of validation errors (Pydantic)
        if (Array.isArray(detail)) {
          message = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join(', ');
        } 
        // Handle string error message
        else if (typeof detail === 'string') {
          message = detail;
        }
        // Handle object error
        else if (typeof detail === 'object' && detail.msg) {
          message = detail.msg;
        }
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      toast.success("¡Cuenta creada exitosamente!");
      return { success: true };
    } catch (error) {
      let message = "Error al crear cuenta";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle array of validation errors (Pydantic)
        if (Array.isArray(detail)) {
          message = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join(', ');
        } 
        // Handle string error message
        else if (typeof detail === 'string') {
          message = detail;
        }
        // Handle object error
        else if (typeof detail === 'object' && detail.msg) {
          message = detail.msg;
        }
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success("Sesión cerrada");
  };

  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/games/orisha" 
              element={
                <ProtectedRoute>
                  <OrishaGame />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/games/signs" 
              element={
                <ProtectedRoute>
                  <IfaSignsGame />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/games/rituals" 
              element={
                <ProtectedRoute>
                  <RitualsGame />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/games/yoruba" 
              element={
                <ProtectedRoute>
                  <ComingSoon 
                    gameName="Idioma Yoruba" 
                    gameIcon="🗣️" 
                    description="Estamos creando una experiencia interactiva completa para aprender el idioma sagrado Yoruba con pronunciación, vocabulario esencial, y ejercicios prácticos."
                  />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/games/sureye" 
              element={
                <ProtectedRoute>
                  <ComingSoon 
                    gameName="Sureye y Patakíes" 
                    gameIcon="📚" 
                    description="Próximamente podrás explorar las historias sagradas (Patakíes) y sus enseñanzas profundas, con narraciones interactivas y lecciones de sabiduría ancestral."
                  />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              } 
            />
            
            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;